import * as React from 'react';
import {ELiteral} from '@packages/systems/wfdl-ast';
import {
  GuideIcon,
  HStack,
  PageDefaultIcon,
  RefreshIcon,
  View,
} from '@packages/systems/spring';
import {
  getMeta,
  Plugins,
  Types,
  TypeImplementation,
  Expressions,
} from '@packages/systems/wfdl';
import {
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {prettySize, truncateMid} from '@packages/utilities/string-utils';
import {
  styled,
  useTheme,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {selectSelectedElement} from '@packages/utilities/platform-selectors';
import {Text} from '@packages/systems/design-system/Text';
import {Box} from '@packages/systems/design-system/Box';
import Button from '@packages/systems/design-system/Button';
import {Grid} from '@packages/systems/design-system/Grid';
import {HEAVY_BYTES} from '@packages/utilities/file-upload';
import {isImageAsset} from '@packages/domains/assets/utilities';
import {getDocIcon} from '@packages/shared/utils/AssetUtils';
import {WarningXsmall as WarningIcon} from '@packages/systems/ui/icons';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {isImageElement} from '@packages/utilities/elementTypeUtils';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {IMAGE_PLACEHOLDER_FULL_ID} from '@packages/systems/plugins-utils/Asset';
import {useUploadFromDevice} from './useUploadFromDevice';

const basicPlugin = Plugins.name('Basic');

const StyledWarning = styled(Box)(({theme}) => ({color: theme.colors.warning}));

const ImagePreview = emotionStyled('img')({
  gridColumn: '1 / 2',
  gridRow: '1 / -1',
  background: tokens.colors.background4,
  width: '100%',
  height: '100%',
  borderRadius: tokens.controls.borderRadius,
  borderWidth: tokens.controls.borderWidth,
  borderStyle: 'solid',
  borderColor: tokens.colors.border3,
  objectFit: 'contain',
});

const oneLineText = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

type State = {
  id?: string;
  url?: string;
};

type Meta = {
  type: 'image' | 'all';
};

const defaultState: State = Object.freeze({});

const defaultImageState: State = Object.freeze({id: IMAGE_PLACEHOLDER_FULL_ID});

const defaultMeta: Meta = {type: 'all'};

const selectAssetStore = (state: DesignerStores) => state.AssetStore;

const MetaIcon = emotionStyled(PageDefaultIcon)({
  marginRight: tokens.space[1],
});

const imageSourcePath = [
  {in: 'Record' as const, at: 'attr'},
  {in: 'Record' as const, at: 'src'},
];

const getImagePreviewUrl = ({
  state,
  selectedElement,
}: {
  state: State;
  selectedElement?: ExpressionElement | null;
}): string | null => {
  if (typeof state.url === 'string' && state.url.length > 0) {
    return state.url;
  }

  if (!selectedElement || !isImageElement(selectedElement)) {
    return null;
  }

  const srcExpr = Expressions.getIn(selectedElement.data, imageSourcePath);
  const src = srcExpr ? Expressions.getText(srcExpr) : null;

  return src && src.length > 0 ? src : null;
};

const getImagePreviewFallbackAsset = ({
  state,
  selectedElement,
}: {
  state: State;
  selectedElement?: ExpressionElement | null;
}) => {
  const previewUrl = getImagePreviewUrl({state, selectedElement});

  if (!previewUrl) {
    return null;
  }

  return {
    _id: state.id ?? previewUrl,
    origFileName: 'img preview',
    fileSize: 0,
    width: 0,
    height: 0,
    cdnUrl: previewUrl,
  };
};

// @ts-expect-error - TS7031 - Binding element 'type' implicitly has an 'any' type. | TS7031 - Binding element 'asset' implicitly has an 'any' type. | TS7031 - Binding element 'isOverride' implicitly has an 'any' type.
const PreviewContents = ({type, asset, isOverride}) => {
  let previewContent;
  let metaContent;

  if (
    type === 'image' ||
    !asset ||
    isImageAsset(asset) ||
    // HACK: We don't set `mimeType` on our placeholder image asset, so we
    // need to explicitly treat it as an image here:
    asset._id === IMAGE_PLACEHOLDER_FULL_ID
  ) {
    const sourceFileName = asset ? asset.origFileName : '';
    const sourceSize = asset ? asset.fileSize : 0;
    const sourceWidth = asset ? asset.width : 0;
    const sourceHeight = asset ? asset.height : 0;
    const src = asset ? asset.thumbUrl || asset.cdnUrl || asset.s3Url : '';
    const hasDimensions = sourceWidth > 0 && sourceHeight > 0;
    const hasFileSize = sourceSize > 0;

    previewContent = <ImagePreview src={src} alt="img preview" />;
    metaContent = (
      <>
        <HStack spacing={1} align="center" style={oneLineText}>
          {asset?.origin?.length ? (
            <GuideIcon colorVariant="secondary" />
          ) : null}
          <Text data-automation-id={sourceFileName} style={oneLineText}>
            {sourceFileName}
          </Text>
        </HStack>
        {hasDimensions ? (
          <View display="flex" flexDirection="row">
            {isOverride ? <MetaIcon size={12} /> : null}
            <Text color="foregroundDim">
              {sourceWidth} x {sourceHeight}px
            </Text>
          </View>
        ) : null}
        {hasFileSize ? (
          <View display="flex" flexDirection="row" alignItems="center">
            {isOverride ? <MetaIcon size={12} /> : null}
            <Text
              color="foregroundDim"
              display="inline-grid"
              gridAutoFlow="column"
              gridGap={1}
              alignItems="center"
              justifyContent="start"
            >
              {prettySize(sourceSize)}{' '}
              {sourceSize > HEAVY_BYTES ? (
                <StyledWarning
                  // We should be using some kind of tooltip trigger instead
                  title="This image file is very large, and will make your site loadtime slower. Try replacing it with a smaller image."
                >
                  <WarningIcon />
                </StyledWarning>
              ) : null}
            </Text>
          </View>
        ) : null}
      </>
    );
  } else {
    const PreviewIcon =
      asset && (asset.cdnUrl || asset.origFileName)
        ? getDocIcon(asset.cdnUrl || asset.origFileName, 'medium')
        : null;

    const assetName = asset ? truncateMid(asset.origFileName, 32) : null;
    const sizeText = asset ? prettySize(asset.fileSize) : null;

    previewContent = PreviewIcon && (
      <PreviewIcon style={{width: '100%', height: '100%'}} />
    );
    metaContent = (
      <>
        <Text style={oneLineText}>{assetName}</Text>
        {sizeText && <Text color="foregroundDim">{sizeText}</Text>}
      </>
    );
  }

  return (
    <Grid
      gridTemplateColumns="auto 1fr"
      gridColumn="1 / -1"
      gridRowGap={0}
      gridColumnGap={2}
      alignItems="center"
    >
      <Box
        width={isOverride ? 64 : 48}
        height={isOverride ? 64 : 48}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {previewContent}
      </Box>
      <Grid gridGap={1}>{metaContent}</Grid>
    </Grid>
  );
};
const createLiteralExpr = (
  value: {
    id: string;
    url?: string;
  },
  // @ts-expect-error - TS7006 - Parameter 'origin' implicitly has an 'any' type.
  origin
) =>
  ELiteral({
    name: qualify(Plugins.name('Asset'), typeName(origin)),
    value,
  });

const AssetSettings = ({
  expr,
  dispatch,
  origin,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
  origin: string;
}) => {
  const ty = getAnn(expr).type;
  const meta: Meta = (ty && (getMeta(ty) as any)) || defaultMeta;
  // HACKish: Technically we can't really know what the name of our plugin can
  // be from this file, so we rely on `dataType.meta` to distinguish between
  // general assets and images. Fall back to the TypeConstructor name when meta
  // is absent (e.g. code component Image props compiled without withMeta).
  const type: Meta['type'] =
    meta.type !== 'all'
      ? meta.type
      : ty != null && Types.isImageType(ty)
        ? 'image'
        : meta.type;

  const theme = useTheme();

  const {env} = useExpressionEditorImplementationContext();
  const selectedElement = useDesignerSelector(selectSelectedElement);
  const selecting = env.asset.isSelecting();
  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  const abortChoose = React.useCallback(env.asset.abortChoose, []);

  // Track whether we just initiated asset selection. When asset selection starts,
  // the Assets panel opens, which may cause the parent panel to close and unmount
  // this component. We should NOT abort the selection in this case since we
  // intentionally started it.
  const justInitiatedSelectionRef = React.useRef(false);

  const {
    canUploadFromDevice,
    isUploading: isUploadingFromDevice,
    chooseFromDevice,
  } = useUploadFromDevice({
    accept: type,
    onUploaded(uploadedAsset) {
      dispatch(
        updateExpressionAction(
          createLiteralExpr(
            {id: uploadedAsset._id, url: uploadedAsset.cdnUrl},
            origin
          )
        )
      );
    },
  });

  React.useEffect(() => {
    return () => {
      // Only abort if we were selecting AND we didn't just initiate the selection.
      if (selecting && !justInitiatedSelectionRef.current) {
        abortChoose();
      }
    };
  }, [abortChoose, selecting]);

  const literal = ContextualExpressions.getLiteral(stripAnn(expr))?.value;
  if (!literal || !ty) {
    return null;
  }
  // @ts-expect-error - TS2322 - Type 'string | number | true | Json[] | { [key: string]: Json; }' is not assignable to type 'State'.
  const state: State = literal;

  const resolvedAsset = state.id
    ? env.asset.get({type, id: state.id})
    : env.asset.get({type, id: IMAGE_PLACEHOLDER_FULL_ID});
  const asset =
    resolvedAsset ||
    (type === 'image'
      ? getImagePreviewFallbackAsset({
          state,
          selectedElement,
        })
      : null);
  const isAssetChosen = Boolean(
    asset && asset._id !== IMAGE_PLACEHOLDER_FULL_ID
  );

  const humanReadableType = type === 'image' ? 'Image' : 'Asset';
  const label = isAssetChosen ? 'Replace' : 'Choose';

  const chooseAsset = () => {
    // Mark that we're intentionally initiating selection. This prevents the
    // cleanup effect from aborting when this component unmounts due to the
    // Assets panel opening.
    justInitiatedSelectionRef.current = true;

    env.transaction.begin();

    env.asset.choose({
      type,
      onChange(chosenAsset: any) {
        const newId = chosenAsset._id;
        dispatch(
          updateExpressionAction(createLiteralExpr({id: newId}, origin))
        );
      },

      onCommit() {
        justInitiatedSelectionRef.current = false;
        env.transaction.commit();
      },

      onCancel() {
        // Called when the Assets panel is closed without completing selection.
        // This clears the assetSelectState so isSelecting() returns false.
        justInitiatedSelectionRef.current = false;
        abortChoose();
      },
    });
  };

  // Image props in dynamic rich text upload straight to the CMS database via
  // this env capability; the picker/upload live in the app layer because
  // shared/render can't import designer-flux's FileInput.
  const shouldUploadFromDevice = type === 'image' && canUploadFromDevice;

  const isOverride = env.context === 'override' || false;

  return (
    <Grid
      gridGap={2}
      style={{
        backgroundColor: theme.colors.backgroundVivid,
        borderColor: theme.colors.border,
        borderRadius: isOverride ? 4 : 2,
        borderStyle: 'solid',
        borderWidth: 1,
        padding: isOverride ? 8 : 4,
      }}
    >
      <PreviewContents type={type} asset={asset} isOverride={isOverride} />

      <Button
        data-automation-id={Types.generateAutomationIds(ty)}
        onClick={shouldUploadFromDevice ? chooseFromDevice : chooseAsset}
        disabled={selecting || isUploadingFromDevice}
        compact
        style={{gridColumn: '1 / -1'}}
      >
        {isOverride && isAssetChosen && !isUploadingFromDevice ? (
          <View marginRight="$0.5">
            <RefreshIcon />
          </View>
        ) : null}
        {isUploadingFromDevice
          ? 'Uploading…'
          : `${label} ${humanReadableType}${isOverride ? '' : '...'}`}
      </Button>
    </Grid>
  );
};

export const AssetPluginType = TypeImplementation.Custom({
  render: ({expr, dispatch}) => (
    <AssetSettings expr={expr} dispatch={dispatch} origin="Asset" />
  ),
  system: {
    defaultValue: defaultState,
    label: 'Asset',
  },
  icon: TypeImplementation.IconByName(
    qualify(basicPlugin, iconName('Attachment'))
  ),
});

export const ImagePluginType = TypeImplementation.Custom({
  renderSummary: function ImagePluginTypeRenderSummary({
    expr,
  }: {
    expr: ContextualExpression;
  }) {
    const assetStore = useDesignerSelector(selectAssetStore);
    const literal = ContextualExpressions.getLiteral(stripAnn(expr));
    const literalValue = literal?.value;

    if (
      !literalValue ||
      typeof literalValue !== 'object' ||
      !('id' in literalValue) ||
      typeof literalValue.id !== 'string'
    ) {
      return <span>Missing image</span>;
    }

    const imageId = literalValue.id;

    if (imageId === IMAGE_PLACEHOLDER_FULL_ID) {
      return <span>Placeholder</span>;
    }

    const asset = assetStore.getById(imageId);
    const fileName = asset ? asset.origFileName : null;
    if (!fileName) {
      return <span>Missing image</span>;
    }
    const truncatedFileName = truncateMid(fileName, 30);
    return <span>{truncatedFileName}</span>;
  },
  render: ({expr, dispatch}) => (
    <AssetSettings expr={expr} dispatch={dispatch} origin="Image" />
  ),
  system: {
    defaultValue: defaultImageState,
    label: 'Image',
  },
  icon: TypeImplementation.IconByName(qualify(basicPlugin, iconName('Image'))),
});
